import { MMButton, RecurringStatusBadge } from '@components'
import cn from '@integrations/classnames'
import { useContext } from '@integrations/react'
import { formatDisplayDate, getRecurringFrequencyDescription } from '@utils'
import type { PortalRecurringPlanView } from 'mmfintech-backend-api/types'
import { formatMoney, GlobalContext, tr } from 'mmfintech-commons'
import { LabelValue } from 'mmfintech-portal-commons'
import styles from './RecurringDetailsModal.module.scss'
import { RecurringModalCard, RecurringModalFooter, RecurringModalSurface } from './RecurringModalSurface'

interface Props {
  plan: PortalRecurringPlanView
}

function formatDetailDate(date?: string) {
  return formatDisplayDate(date) || '—'
}

function RecurringDetailsModal({ plan }: Props) {
  const { modalHide } = useContext(GlobalContext)

  const {
    id,
    recurringPlanName,
    startDate,
    recurringStep,
    recurringPlanStatus,
    nextBillingDate,
    recurringAmount,
    currency,
    paymentMethod,
    lastExecutionDate,
    recipient,
    accountName
  } = plan || {}

  return (
    <RecurringModalSurface
      title={tr('FRONTEND.RECURRING.DETAILS.TITLE', 'Recurring details')}
      className={cn('recurring-details-modal-wrapper')}
      data-test='recurring-details-modal'
    >
      <RecurringModalCard amount className={cn('recurring-details-card recurring-details-amount-card')}>
        <div className={cn('amount')} data-test='recurring-details-amount'>
          {formatMoney(recurringAmount, currency)}
        </div>
        <RecurringStatusBadge status={recurringPlanStatus} />
      </RecurringModalCard>

      <RecurringModalCard className={cn('recurring-details-card')}>
        <LabelValue
          label={tr('FRONTEND.RECURRING.DETAILS.ID', 'ID')}
          value={id}
          colon
          dataTest='recurring-details-id'
        />
        <LabelValue
          label={tr('FRONTEND.RECURRING.DETAILS.NAME', 'Recurring plan name')}
          value={recurringPlanName}
          colon
          dataTest='recurring-details-name'
        />
        <LabelValue
          label={tr('FRONTEND.RECURRING.DETAILS.PAYMENT_METHOD', 'Payment method')}
          value={tr(`METADATA.PAYMENT_OPTIONS.${paymentMethod}`, paymentMethod)}
          colon
          dataTest='recurring-details-payment-method'
        />
        <LabelValue
          label={tr('FRONTEND.RECURRING.DETAILS.RECIPIENT', 'Recipient')}
          value={recipient}
          colon
          dataTest='recurring-details-recipient'
        />
        <LabelValue
          label={tr('FRONTEND.RECURRING.DETAILS.ACCOUNT', 'Account')}
          value={accountName}
          colon
          dataTest='recurring-details-account'
        />

        <div
          className={cn(styles['recurring-details-compound-row'], 'recurring-details-compound-row')}
          data-test='recurring-details-transfer'
        >
          <div className={cn(styles['label'], 'label')}>
            {tr('FRONTEND.RECURRING.DETAILS.RECURRING_TRANSFER', 'Recurring transfer')}
          </div>
          <div className={cn(styles['value'], 'value')}>
            <span className={cn(styles['pair'], 'pair')}>
              <b>{tr('FRONTEND.RECURRING.DETAILS.START_DATE', 'Start Date:') || 'Start Date:'}</b>
              <span>{formatDetailDate(startDate)}</span>
            </span>
            <span className={cn(styles['arrow'])}>→</span>
            <span className={cn(styles['pair'], 'pair')}>
              <b>{tr('FRONTEND.RECURRING.DETAILS.FREQUENCY', 'Frequency:') || 'Frequency:'}</b>
              <span>{getRecurringFrequencyDescription(startDate, recurringStep)}</span>
            </span>
          </div>
        </div>

        <div
          className={cn(styles['recurring-details-compound-row'], 'recurring-details-compound-row')}
          data-test='recurring-details-execution'
        >
          <div className={cn(styles['label'], 'label')}>
            {tr('FRONTEND.RECURRING.DETAILS.EXECUTION_DATES', 'Execution dates')}
          </div>
          <div className={cn(styles['value'], 'value')}>
            <span className={cn(styles['pair'], 'pair')}>
              <b>{tr('FRONTEND.RECURRING.DETAILS.LAST_EXECUTION', 'Last Execution:') || 'Last Execution:'}</b>
              <span>{formatDetailDate(lastExecutionDate)}</span>
            </span>
            <span className={cn(styles['arrow'])}>→</span>
            <span className={cn(styles['pair'], 'pair')}>
              <b>{tr('FRONTEND.RECURRING.DETAILS.NEXT', 'Next:') || 'Next:'}</b>
              <span>{formatDetailDate(nextBillingDate)}</span>
            </span>
          </div>
        </div>
      </RecurringModalCard>

      <RecurringModalFooter className={cn('recurring-details-modal-footer')}>
        <MMButton
          type='button'
          variant='dashboard'
          caption={tr('FRONTEND.BUTTONS.CLOSE', 'Close')}
          onClick={modalHide}
          data-test='button-close-recurring-details'
        />
      </RecurringModalFooter>
    </RecurringModalSurface>
  )
}

export default RecurringDetailsModal
